import { getItem, setItem, removeItem } from '../platform/storage';

const KEY = 'aqana.station.config';

// Station identities.
//
// ⚠️ TABLE_SERVICE's stored value is 'waiter' and MUST STAY 'waiter'. Devices
// already enrolled in the field hold {"station":"waiter"} under the KEY below;
// getStationConfig returns that value verbatim and PosAppShell switches on it,
// so renaming the value would strand every enrolled waiter device on a station
// the shell does not recognise, with no migration path. Only the LABEL changed
// (F15 part 2) — the surface it mounts, TablesSurface, has always been Table
// Service (floor plan, waitlist, reservations), not waiter order-taking. See
// stations/WaiterStation.js:9-15, which documents the original mismatch.
//
// WAITER is kept as a deprecated alias of the same value so existing imports
// keep working; new code should use TABLE_SERVICE.
export const STATIONS = {
  CASHIER: 'cashier',
  WAITER: 'waiter',          // deprecated alias of TABLE_SERVICE
  TABLE_SERVICE: 'waiter',   // ⚠️ stored value — do not change
  WAITER_SALES: 'waiter_sales',
  KDS: 'kds',
};

// Device Printer Config Task 7: a device can now be assigned a printer per
// role, not just one printer total — the common layout is one till driving
// a receipt, kitchen, AND bar printer, none of which fit in a single
// printerId. This is the shape every reader/writer normalises toward.
export const DEFAULT_PRINTER_IDS = { receipt: null, kitchen: null, bar: null };

export async function getStationConfig() {
  const raw = await getItem(KEY);
  if (!raw) return null;
  const parsed = JSON.parse(raw);
  // Forward-compat: devices already in the field have a stored config
  // written before printerId/printerIds existed. Normalise the missing
  // keys instead of leaving them undefined. `printerId` (legacy scalar) is
  // kept around unchanged, read-only, purely as a fallback for devices
  // assigned before per-role assignment existed — see PrintingUtils.js's
  // resolveAssignedPrinterId. Nothing writes a new value into it anymore.
  return {
    ...parsed,
    printerId: parsed.printerId || null,
    printerIds: { ...DEFAULT_PRINTER_IDS, ...(parsed.printerIds || {}) },
  };
}

export async function setStationConfig(cfg) {
  // The printer assignment must survive saves that don't mention it (e.g.
  // re-saving the station from the settings sheet), so read the existing
  // value forward unless this call explicitly sets/clears printerId(s).
  const existing = await getStationConfig();
  const normalized = {
    station: cfg.station,
    kdsQueue: cfg.station === STATIONS.KDS ? (cfg.kdsQueue || 'kitchen') : null,
    backendEnv: cfg.backendEnv || 'prod',
    // Belt-and-braces: the tenant this device enrolled against, persisted
    // independently of AuthContext. Falls back into useOperatorSession if
    // AuthContext's tenantId is ever empty (e.g. an unforeseen logout path)
    // so the station can't brick even when AuthContext state is wrong.
    tenantId: cfg.tenantId || null,
    printerId: cfg.printerId !== undefined ? (cfg.printerId || null) : (existing ? existing.printerId : null),
    // `cfg.printerIds` may be a partial map (a single role changed) — merge
    // it onto whatever is already stored (or the all-null default) rather
    // than replacing the whole map, so saving one role never clobbers the
    // others. A role explicitly set to null clears just that role.
    printerIds: cfg.printerIds !== undefined
      ? { ...(existing ? existing.printerIds : DEFAULT_PRINTER_IDS), ...cfg.printerIds }
      : (existing ? existing.printerIds : DEFAULT_PRINTER_IDS),
  };
  await setItem(KEY, JSON.stringify(normalized));
}

export async function clearStationConfig() {
  await removeItem(KEY);
}
